import { useEffect, useId, useRef, useState } from 'react';

import { useCustomer, useCustomers } from './api.js';

const CONTROL_CLASS =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500';

interface CustomerSelectFieldProps {
  label?: string;
  /** Currently selected customer id (empty string when none). */
  value: string;
  onChange: (customerId: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string | undefined;
  placeholder?: string;
  /** When true, only active customers are searchable. */
  activeOnly?: boolean;
  /** When true, shows an “All customers” option that clears the selection (filter mode). */
  allowClear?: boolean;
}

/**
 * A searchable customer picker. Typing filters the list server-side, so every
 * customer is reachable regardless of how many exist. The selected customer's
 * name is resolved independently of the current search so it always displays.
 */
export function CustomerSelectField({
  label,
  value,
  onChange,
  required,
  disabled,
  error,
  placeholder = 'Select a customer…',
  activeOnly,
  allowClear,
}: CustomerSelectFieldProps): JSX.Element {
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const listQuery = useCustomers({
    page: 1,
    pageSize: 20,
    search: debounced || undefined,
    // A searchable picker must reach every customer regardless of type, so it uses the
    // 'all' scope (the default list scope hides prospect/CW-imported types).
    scope: 'all',
    ...(activeOnly ? { isActive: true } : {}),
  });

  const selectedQuery = useCustomer(value || undefined);
  const selectedName = value ? (selectedQuery.data?.name ?? '') : '';

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocPointerDown);
    return () => document.removeEventListener('mousedown', onDocPointerDown);
  }, [open]);

  const openMenu = (): void => {
    if (disabled) {
      return;
    }
    setOpen(true);
    setSearch('');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const selectCustomer = (customerId: string): void => {
    onChange(customerId);
    setOpen(false);
    setSearch('');
  };

  const results = listQuery.data?.data ?? [];

  return (
    <div ref={containerRef} className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={id} className="text-sm font-medium text-slate-700">
          {label}
          {required ? <span className="ml-0.5 text-rose-600">*</span> : null}
        </label>
      ) : null}
      <div className="relative">
        {open ? (
          <input
            id={id}
            ref={inputRef}
            type="text"
            className={CONTROL_CLASS}
            placeholder="Type to search customers…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setOpen(false);
                setSearch('');
              }
            }}
            autoComplete="off"
          />
        ) : (
          <button
            id={id}
            type="button"
            disabled={disabled}
            onClick={openMenu}
            className={`${CONTROL_CLASS} flex items-center justify-between text-left`}
          >
            <span className={selectedName ? 'text-slate-900' : 'text-slate-400'}>
              {selectedName || placeholder}
            </span>
            <svg
              className="h-4 w-4 shrink-0 text-slate-400"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        {open ? (
          <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {listQuery.isLoading ? (
              <li className="px-3 py-2 text-sm text-slate-400">Loading…</li>
            ) : results.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-400">No customers found</li>
            ) : (
              <>
                {allowClear ? (
                  <li>
                    <button
                      type="button"
                      onClick={() => selectCustomer('')}
                      className={`flex w-full items-center px-3 py-2 text-left text-sm hover:bg-slate-100 ${
                        value === '' ? 'font-medium text-brand-700' : 'text-slate-500'
                      }`}
                    >
                      All customers
                    </button>
                  </li>
                ) : null}
                {results.map((customer) => (
                  <li key={customer.id}>
                    <button
                      type="button"
                      onClick={() => selectCustomer(customer.id)}
                      className={`flex w-full items-center px-3 py-2 text-left text-sm hover:bg-slate-100 ${
                        customer.id === value ? 'font-medium text-brand-700' : 'text-slate-900'
                      }`}
                    >
                      {customer.name}
                    </button>
                  </li>
                ))}
              </>
            )}
          </ul>
        ) : null}
      </div>
      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}
